import { Close } from "@mui/icons-material";
import { Box, FormControl, Grid, MenuItem, Select } from "@mui/material";
import { useFormikContext } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getSessionUUID } from "../../../../utils/uuidManager";
import { toast } from "react-toastify";
import { useWebSocket } from "../../../../contexts/WebSocketContext";
import { useGeneratePostIntroductionMutation } from "../../../../redux/slices/featuresApiSlice";
import BotBlogrButton from "../../../dashboard/BotBlogrButton";
import { useGenerationErrorHandling } from "../../../errorHandling";
import { trackPlatformUsed } from "../../../trackers";
import { GenerationButton } from "../../FeaturesTemplate/FeaturesFields/GenerationButton";
import GlobalTextField from "../../FeaturesTemplate/FeaturesFields/GlobalTextField";
import RewriteModal from "../../SidePanel/global-rewrite-modal/rewrite-modal";

const IntroductionModal = ({ isIntroGenerated }) => {
  const { t } = useTranslation();
  const [customize, setCustomize] = useState(false);
  const userInfo = useSelector((state) => state?.auth.userInfo);

  const {
    introHeading,
    setOpenIntroductionModal,
    openIntroductionRewrite,
    setOpenIntroductionRewrite,
    bufferRef,
  } = useWebSocket();

  const [generatePostIntroduction, { isLoading }] =
    useGeneratePostIntroductionMutation();

  const handleGenerationError = useGenerationErrorHandling();
  const formikProps = useFormikContext();

  const handleGenerateIntroduction = async () => {
    trackPlatformUsed("introduction", "ai_generation");

    // Reset buffer before starting new generation
    bufferRef.current = "";

    try {
      let payload = {
        id: formikProps.values.id || null,
        style_id: formikProps.values.style_id || null,
        company_id: formikProps.values.company_id || null,
        product_id: formikProps.values.product_id || null,
        subject: formikProps.values.subject,
        seo_request: formikProps.values.seo_request,
        outline: formikProps.values.outline,
        length: formikProps.values.introduction_length,
      };

      if (isIntroGenerated) {
        payload["introduction"] = formikProps.values.introduction;
      } else {
        introHeading.current = formikProps.values.introduction;
      }

      // Add customize_prompt if customize is true
      if (customize && formikProps.values.customize_prompt?.trim()) {
        payload["customize_prompt"] = formikProps.values.customize_prompt;
      }

      console.log("Starting introduction generation with streaming...");

      const uuid = userInfo?.uuid || getSessionUUID();
      const res = await generatePostIntroduction({
        ...payload,
        uuid,
        stream: true,
      }).unwrap();

      if (res?.status !== 200) {
        toast.error(
          t(
            "something-went-wrong-with-generation",
            "Une erreur s'est produite lors de la génération"
          )
        );
        return;
      }

      // For streaming, the content will be handled by WebSocket context
      // Only handle non-streaming fallback here
      if (!res?.stream && res?.data) {
        if (!isIntroGenerated) {
          const introzone = document.getElementById("introzone");
          if (introzone) {
            introzone.innerHTML = introHeading.current + res?.data;
          }
        } else {
          formikProps.setFieldValue("introduction_rewrite", res?.data);
        }

        introHeading.current = "";
      }
    } catch (error) {
      handleGenerationError(error);
      toast.error("Introduction generation failed. Please try again.");
    }
  };

  const saveSelectedVersion = (data) => {
    const zone = document.getElementById("introzone");
    if (zone) {
      zone.innerHTML = data;
    }
    formikProps.setFieldValue("introduction_rewrite", "");
    setOpenIntroductionRewrite(false);
    setOpenIntroductionModal(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        height: "100vh",
        width: {
          lg: "calc(100vw - 500px)",
          md: "calc(100vw - 350px)",
          xs: "100%",
        },
        display: "flex",
        mt: { md: "48px !important", xs: "unset" },
        left: 0,
        top: 0,
        bgcolor: "#000000a1",
        zIndex: 99999,
      }}
    >
      {openIntroductionRewrite ? (
        <RewriteModal
          previous={formikProps.values.introduction}
          rewritten={formikProps.values.introduction_rewrite}
          save={saveSelectedVersion}
          label={t("Rewrite-introduction", "Rewrite the introduction")}
          setOpenModal={setOpenIntroductionRewrite}
          rewriteFieldName="introduction_rewrite"
        />
      ) : (
        <div className="m-auto bg-white rounded-xl w-[80%] max-w-[500px]">
          <div className="flex justify-between p-4 items-center border-b-[3px] border-slate-200">
            <p>
              {!isIntroGenerated
                ? t("Generate-introduction", "Generate the introduction")
                : t("Rewrite-introduction", "Rewrite the introduction")}
            </p>
            <Close
              onClick={() => {
                // reset toggle + input + validation state
                setCustomize(false);
                formikProps.setFieldValue("customize_prompt", "");
                formikProps.setFieldTouched("customize_prompt", false);

                setOpenIntroductionModal(false);
              }}
              className="cursor-pointer"
              sx={{ fontSize: 32 }}
            />
          </div>
          <div className="p-4">
            <FormControl sx={{ mb: 2 }} fullWidth>
              <p className="mb-2">
                {t("Introduction-length", "Introduction length")}
              </p>
              <Select
                sx={{ bgcolor: "white" }}
                value={formikProps.values.introduction_length}
                onChange={(e) =>
                  formikProps.setFieldValue(
                    "introduction_length",
                    e.target.value
                  )
                }
              >
                <MenuItem value={"courte"}>{t("Short", "Courte")}</MenuItem>
                <MenuItem value={"moyenne"}>{t("Medium", "Moyenne")}</MenuItem>
                <MenuItem value={"longue"}>{t("Long", "Longue")}</MenuItem>
              </Select>

              {/* Toggle switch and conditional textarea */}
              <div className="pt-0 px-0">
                <div className="mt-4 inline-flex items-center h-6">
                  <label className="inline-flex items-center cursor-pointer relative">
                    <input
                      type="checkbox"
                      name="customize"
                      checked={customize}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setCustomize(checked);

                        // ✅ clear input when turning off
                        if (!checked) {
                          formikProps.setFieldValue("customize_prompt", "");
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
                  </label>
                  <span className="ms-3 text-sm">
                    {t("custom-instructions", "Instructions personnalisées")}
                  </span>
                </div>
                {customize && (
                  <Grid item lg={12} xs={12}>
                    <p>
                      {t("Instruction", "Instruction")}
                      <span className="text-red-500 ml-1">*</span>
                    </p>
                    <GlobalTextField
                      formikProps={formikProps}
                      name={"customize_prompt"}
                      placeholder={t(
                        "Ex: Make it more formal",
                        "Ex: Rends-le plus formel"
                      )}
                      isRequired={true}
                      multipleLines={true}
                      maxLength={1000}
                    />
                  </Grid>
                )}
              </div>
            </FormControl>

            <div className="flex items-center gap-4">
              <GenerationButton
                formikProps={formikProps}
                text={t("Generate-introduction", "Générer une introduction")}
                handleGenerate={handleGenerateIntroduction}
                isLoading={isLoading}
                platform="openai"
                customize={customize}
              />

              {formikProps.values.introduction_rewrite !== "" && (
                <BotBlogrButton
                  onClick={() => setOpenIntroductionRewrite(true)}
                  variant="violet"
                  className="!bg-violet-400 hover:!bg-violet-500 !normal-case !text-white !px-4 !py-2"
                >
                  {t("View-rewritten-version", "Voir la version réécrite")}
                </BotBlogrButton>
              )}
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default IntroductionModal;
